import { useRef, useEffect, useState } from 'react';
import './PortalIframe.css';

interface Props {
  /** URL of the preview route, e.g. "/preview/dashboard" */
  src: string;
  /** Natural height of the page at 1200px viewport width */
  naturalHeight: number;
  /** URL shown in the fake browser chrome */
  displayUrl?: string;
  className?: string;
}

/**
 * Renders a real portal page inside a browser-chrome frame.
 * Uses ResizeObserver to dynamically compute the CSS scale so it
 * fills the container at every viewport size without overflowing.
 */
export function PortalIframe({ src, naturalHeight, displayUrl, className = '' }: Props) {
  const wrapRef   = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setScale(w / 1200);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const visibleHeight = Math.round(naturalHeight * scale);
  const url = displayUrl ?? src.replace('/preview/', 'scholar-sphere.com/');

  return (
    <div className={`portal-iframe-outer ${className}`}>
      {/* Browser chrome */}
      <div className="portal-iframe-chrome">
        <div className="portal-iframe-dots">
          <span style={{ background: '#ff5f57' }} />
          <span style={{ background: '#febc2e' }} />
          <span style={{ background: '#28c840' }} />
        </div>
        <div className="portal-iframe-url">{url}</div>
      </div>

      {/* Scaled viewport */}
      <div
        ref={wrapRef}
        className="portal-iframe-clip"
        style={{ height: visibleHeight }}
      >
        <iframe
          src={src}
          title={url}
          scrolling="no"
          style={{
            border: 'none',
            width: '1200px',
            height: `${naturalHeight}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            pointerEvents: 'none',
            display: 'block',
          }}
        />
      </div>
    </div>
  );
}
